/**
 * Busca de lugares (Places API "New") para o campo de local da atividade.
 *
 * Duas chamadas, como o Google cobra: sugestões enquanto a pessoa digita
 * (barato, com token de sessão) e UMA leitura de detalhes só quando ela
 * escolhe — com os quatro campos que a atividade guarda, e mais nenhum.
 */
import { carregarGoogleMaps } from './google';

export interface Sugestao {
  /** Nome principal ("Museu do Amanhã"). */
  principal: string;
  /** Complemento ("Praça Mauá, 1 - Rio de Janeiro"). */
  secundario: string;
  /** Objeto do Google para pedir os detalhes depois. */
  predicao: google.maps.places.PlacePrediction;
}

export interface LugarEscolhido {
  placeId: string;
  nome: string;
  endereco: string | null;
  latitude: number;
  longitude: number;
}

export type SessaoDeBusca = google.maps.places.AutocompleteSessionToken;

export async function novaSessaoDeBusca(): Promise<SessaoDeBusca | null> {
  const g = await carregarGoogleMaps();
  if (!g) return null;
  const { AutocompleteSessionToken } = (await g.maps.importLibrary('places')) as google.maps.PlacesLibrary;
  return new AutocompleteSessionToken();
}

/**
 * @param apenasRegioes só cidades, estados e países — o "destino principal"
 * de uma viagem é um lugar assim, não um restaurante.
 */
export async function buscarSugestoes(texto: string, sessao: SessaoDeBusca, apenasRegioes = false): Promise<Sugestao[]> {
  const g = await carregarGoogleMaps();
  if (!g) return [];
  const { AutocompleteSuggestion } = (await g.maps.importLibrary('places')) as google.maps.PlacesLibrary;
  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input: texto,
    sessionToken: sessao,
    ...(apenasRegioes ? { includedPrimaryTypes: ['(regions)'] } : {}),
  });
  return suggestions.flatMap((s) => {
    const p = s.placePrediction;
    if (!p) return [];
    return [
      {
        principal: p.mainText?.text ?? p.text.text,
        secundario: p.secondaryText?.text ?? '',
        predicao: p,
      },
    ];
  });
}

export async function detalharLugar(sugestao: Sugestao): Promise<LugarEscolhido | null> {
  const lugar = sugestao.predicao.toPlace();
  await lugar.fetchFields({ fields: ['id', 'displayName', 'formattedAddress', 'location'] });
  return lugarDoPlace({
    id: lugar.id,
    displayName: lugar.displayName ?? null,
    formattedAddress: lugar.formattedAddress ?? null,
    lat: lugar.location?.lat() ?? null,
    lng: lugar.location?.lng() ?? null,
  });
}

/** Parte pura, testável sem Google: o que vira atividade a partir do lugar. */
export function lugarDoPlace(p: {
  id: string;
  displayName: string | null;
  formattedAddress: string | null;
  lat: number | null;
  lng: number | null;
}): LugarEscolhido | null {
  if (p.lat === null || p.lng === null || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return null;
  const nome = (p.displayName ?? '').trim() || (p.formattedAddress ?? '').trim();
  if (!nome) return null;
  return {
    placeId: p.id,
    nome,
    // Endereço igual ao nome não acrescenta nada — e apareceria duas vezes.
    endereco: p.formattedAddress && p.formattedAddress.trim() !== nome ? p.formattedAddress.trim() : null,
    latitude: p.lat,
    longitude: p.lng,
  };
}
